import React, { useState } from "react";
import { SUPERADMIN_USER_ID } from "constants/constants";
import { Divider, Flex, HStack, Icon, IconButton, Text, Tooltip, VStack } from "@chakra-ui/react";
import { AppIcons } from "constants/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useUser } from "context/UserContext";

// ---------------------------------------------------------------------------
// Nav config
// ---------------------------------------------------------------------------

/** Ontology sidebar */
const ONTOLOGY_NAV = [
  { label: "Graph", icon: AppIcons.analystScope, key: "ViewMKG", boxSize: 6 },
  { label: "Metrics", icon: AppIcons.metric, key: "metrics", boxSize: 6 },
  { label: "Measures", icon: AppIcons.measure, key: "measures", boxSize: 6 },
  { label: "Attributes", icon: AppIcons.attribute, key: "attributes", boxSize: 4 },
  { label: "Datetime", icon: AppIcons.timeframe, key: "time", boxSize: 5 },
  { label: "Benchmarks", icon: AppIcons.benchmark, key: "benchmarks", boxSize: 6 },
] as const;

/** Context sidebar */
const CONTEXT_NAV = [
  { label: "Memory", icon: AppIcons.analystMemory, boxSize: 6, key: "memory" },
  // { label: "Company Profile", icon: MdBusiness, boxSize: 5, key: "companyProfile" },
] as const;

export interface ConfigurationNavBarProps {
  activeButton: string;
  setActiveButton: React.Dispatch<React.SetStateAction<string>>;
}

const ConfigurationNavBar: React.FC<ConfigurationNavBarProps> = ({ activeButton, setActiveButton }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useUser();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);

  const goToEntity = (key: string) => {
    setActiveButton(key);
    navigate(`/mkg/${key}`);
  };

  const goToMemory = () => {
    setActiveButton("memory");
    if (location.pathname !== "/mkg/memory") navigate("/mkg/memory");
    setIsSidebarCollapsed(true);
  };

  if (isSidebarCollapsed) {
    return (
      <Flex direction="column" align="center" h="full" pt={3} pb={4} gap={1} w="60px">
        <Tooltip label="Expand sidebar" placement="right" hasArrow bg="gray.700" color="white" fontSize="xs">
          <IconButton
            aria-label="Expand sidebar"
            icon={<Icon as={AppIcons.sidebarOpen} boxSize={7} />}
            variant="ghost"
            size="sm"
            color="gray.400"
            _hover={{ bg: "gray.100", color: "gray.700" }}
            onClick={() => setIsSidebarCollapsed(false)}
          />
        </Tooltip>
        <Divider borderColor="gray.200" mb={2} />

        {/* Sources */}
        <Tooltip label="Models" placement="right" hasArrow bg="gray.700" color="white" fontSize="xs">
          <IconButton
            aria-label="Models"
            icon={<Icon as={AppIcons.dataModel} boxSize={5} />}
            variant="ghost"
            size="sm"
            color={activeButton === "modelList" ? "purple.700" : "gray.500"}
            bg={activeButton === "modelList" ? "purple.50" : "transparent"}
            _hover={{ bg: "gray.100", color: "gray.800" }}
            onClick={() => {
              setActiveButton("modelList");
              navigate("/mkg/models");
            }}
          />
        </Tooltip>
        {userId === SUPERADMIN_USER_ID && (
          <Tooltip label="Connections" placement="right" hasArrow bg="gray.700" color="white" fontSize="xs">
            <IconButton
              aria-label="Connections"
              icon={<Icon as={AppIcons.connection} boxSize={6} />}
              variant="ghost"
              size="sm"
              color={activeButton === "thoughtspotEmbed" ? "purple.700" : "gray.500"}
              bg={activeButton === "thoughtspotEmbed" ? "purple.50" : "transparent"}
              _hover={{ bg: "gray.100", color: "gray.800" }}
              onClick={() => {
                setActiveButton("thoughtspotEmbed");
                navigate("/mkg/thoughtspot");
                setIsSidebarCollapsed(false);
              }}
            />
          </Tooltip>
        )}

        {/* Ontology */}
        <Divider borderColor="gray.200" my={2} />
        {ONTOLOGY_NAV.map(({ label, icon: NavIcon, key, boxSize }) => (
          <Tooltip key={key} label={label} placement="right" hasArrow bg="gray.700" color="white" fontSize="xs">
            <IconButton
              aria-label={label}
              icon={<Icon as={NavIcon} boxSize={boxSize} />}
              variant="ghost"
              size="sm"
              color={activeButton === key ? "purple.700" : "gray.500"}
              bg={activeButton === key ? "purple.50" : "transparent"}
              _hover={{ bg: "gray.100", color: "gray.800" }}
              onClick={() => (key === "ViewMKG" ? (setActiveButton("ViewMKG"), navigate("/mkg")) : goToEntity(key))}
            />
          </Tooltip>
        ))}

        {/* Context */}
        <Divider borderColor="gray.200" my={2} />
        {CONTEXT_NAV.map(({ label, icon: NavIcon, key, boxSize }) => (
          <Tooltip
            key={key}
            label={label === "Memory" ? label : `${label} (coming soon)`}
            placement="right"
            hasArrow
            bg="gray.700"
            color="white"
            fontSize="xs"
          >
            <IconButton
              aria-label={label === "Memory" ? label : `${label} (coming soon)`}
              icon={<Icon as={NavIcon} boxSize={boxSize} />}
              variant="ghost"
              size="sm"
              color={activeButton === key ? "purple.700" : "gray.500"}
              bg={activeButton === key ? "purple.50" : "transparent"}
              _hover={{ bg: "gray.100", color: "gray.800" }}
              onClick={() => (key === "memory" ? goToMemory() : undefined)}
            />
          </Tooltip>
        ))}
      </Flex>
    );
  }

  return (
    <Flex direction="column" h="full" w="220px">
      <VStack spacing={0} align="stretch" px={2} flex={1} overflowY="auto">
        <Flex align="center" justify="space-between" width="full" px={2} pt={2} gap={2}>
          <HStack spacing={2} flex={1} minW={0} align="center">
            <Icon as={AppIcons.knowledge} boxSize={6} color="purple.700" />
            <Text
              fontSize="sm"
              fontWeight="700"
              color="purple.700"
              textTransform="uppercase"
              letterSpacing="wider"
              mb={0}
              textAlign="left"
            >
              Knowledge
            </Text>
          </HStack>
          <Tooltip label="Collapse sidebar" placement="bottom" hasArrow bg="gray.700" color="white" fontSize="xs">
            <IconButton
              aria-label="Collapse sidebar"
              icon={<Icon as={AppIcons.sidebarClose} boxSize={7} />}
              variant="ghost"
              size="sm"
              color="gray.400"
              flexShrink={0}
              _hover={{ bg: "gray.100", color: "gray.600" }}
              onClick={() => setIsSidebarCollapsed(true)}
            />
          </Tooltip>
        </Flex>

        <Flex align="center" width="full" px={2} mt={6}>
          <Text
            fontSize="sm"
            fontWeight="700"
            color="gray.500"
            textTransform="uppercase"
            letterSpacing="wider"
            mb={0}
            textAlign="left"
            lineHeight="1.2"
          >
            Sources
          </Text>
        </Flex>

        <Divider borderColor="gray.300" my={2} />

        <Flex
          as="button"
          align="center"
          width="full"
          px={2}
          py={2}
          borderRadius="md"
          cursor="pointer"
          color={activeButton === "modelList" ? "purple.700" : "gray.700"}
          bg={activeButton === "modelList" ? "purple.50" : "transparent"}
          _hover={{ bg: "gray.100", color: "gray.800" }}
          onClick={() => {
            setActiveButton("modelList");
            navigate("/mkg/models");
          }}
        >
          <Icon
            as={AppIcons.dataModel}
            boxSize={5}
            mr={3}
            minW={6}
            color={activeButton === "modelList" ? "purple.600" : "gray.500"}
          />
          <Text fontSize="sm" mb={0} fontWeight={activeButton === "modelList" ? "700" : "600"} color="inherit">
            Data Models
          </Text>
        </Flex>
        {userId === SUPERADMIN_USER_ID && (
          <Flex
            as="button"
            align="center"
            width="full"
            px={2}
            py={2}
            borderRadius="md"
            cursor="pointer"
            color={activeButton === "thoughtspotEmbed" ? "purple.700" : "gray.700"}
            bg={activeButton === "thoughtspotEmbed" ? "purple.50" : "transparent"}
            _hover={{ bg: "gray.100", color: "gray.800" }}
            onClick={() => {
              setActiveButton("thoughtspotEmbed");
              navigate("/mkg/thoughtspot");
              setIsSidebarCollapsed(true);
            }}
          >
            <Icon
              as={AppIcons.connection}
              boxSize={6}
              mr={3}
              minW={6}
              color={activeButton === "thoughtspotEmbed" ? "purple.600" : "gray.500"}
            />
            <Text fontSize="sm" mb={0} fontWeight={activeButton === "thoughtspotEmbed" ? "700" : "600"} color="inherit">
              Connections
            </Text>
          </Flex>
        )}

        <Flex align="center" width="full" px={2} mt={6}>
          <HStack spacing={0} flex={1} minW={0} align="center">
            <Text
              fontSize="sm"
              fontWeight="700"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="wider"
              mb={0}
              textAlign="left"
              lineHeight="1.2"
            >
              Ontology
            </Text>
          </HStack>
        </Flex>
        <Divider borderColor="gray.300" my={2} />
        {ONTOLOGY_NAV.map(({ label, icon: NavIcon, key, boxSize }) => (
          <Flex
            key={key}
            as="button"
            align="center"
            width="full"
            px={2}
            py={2}
            borderRadius="md"
            cursor="pointer"
            color={activeButton === key ? "purple.700" : "gray.700"}
            bg={activeButton === key ? "purple.50" : "transparent"}
            _hover={{ bg: "gray.100", color: "gray.800" }}
            onClick={() => (key === "ViewMKG" ? (setActiveButton("ViewMKG"), navigate("/mkg")) : goToEntity(key))}
          >
            <Icon
              as={NavIcon}
              boxSize={boxSize}
              mr={3}
              minW={6}
              color={activeButton === key ? "purple.600" : "gray.500"}
            />
            <Text
              fontSize="sm"
              mb={0}
              fontWeight={activeButton === key ? "700" : "600"}
              color="inherit"
              textAlign="left"
            >
              {label}
            </Text>
          </Flex>
        ))}

        <Flex align="center" width="full" px={2} mt={6}>
          <Text
            fontSize="sm"
            fontWeight="700"
            color="gray.500"
            textTransform="uppercase"
            letterSpacing="wider"
            mb={0}
            textAlign="left"
            lineHeight="1.2"
          >
            Context
          </Text>
        </Flex>
        <Divider borderColor="gray.300" my={2} />
        {CONTEXT_NAV.map(({ label, icon: NavIcon, key, boxSize }) => (
          <Flex
            key={key}
            as="button"
            align="center"
            width="full"
            px={2}
            py={2}
            borderRadius="md"
            cursor="pointer"
            color={activeButton === key ? "purple.700" : "gray.700"}
            bg={activeButton === key ? "purple.50" : "transparent"}
            _hover={{ bg: "gray.100", color: "gray.800" }}
            onClick={() => (key === "memory" ? goToMemory() : undefined)}
          >
            <Icon
              as={NavIcon}
              boxSize={boxSize}
              mr={3}
              minW={6}
              color={activeButton === key ? "purple.600" : "gray.500"}
            />
            <Text
              fontSize="sm"
              mb={0}
              fontWeight={activeButton === key ? "700" : "600"}
              color="inherit"
              textAlign="left"
            >
              {label}
            </Text>
          </Flex>
        ))}
      </VStack>
    </Flex>
  );
};

export default ConfigurationNavBar;
